import {
  isChatFlightSavedItemData,
  isCustomFlightSavedItemData,
  isLeFlightSavedItemData,
  type LeFlightSavedItemData,
  type TransportOption,
} from 'tripPlanner/api/types'
import { isRecord } from 'lib/object/objectUtils'
import { parseFlightSearchParams as parseSearchParams } from 'tripPlanner/components/Chat/Flights/parseFlightSearchParams'
import type { RefreshedFlight } from './flightShortlistTypes'

function isFlightOptionBooked(option: TransportOption): boolean {
  const savedItemData = option.savedItemData
  const isSavedItemDataBooked = (
    isLeFlightSavedItemData(savedItemData) ||
    isChatFlightSavedItemData(savedItemData) ||
    isCustomFlightSavedItemData(savedItemData)
  ) && savedItemData.isBooked === true
  return ('isBooked' in option && option.isBooked === true) || isSavedItemDataBooked
}

function isBookedFlightOption(option: TransportOption): boolean {
  return isFlightOptionBooked(option) || (!!option.returnLeg && isFlightOptionBooked(option.returnLeg))
}

// Recovers currencyCode from the serialized searchParams blob.
function parseSearchParamsCurrency(searchParams: string | undefined): string | undefined {
  if (!searchParams) return undefined
  try {
    const parsed: unknown = JSON.parse(searchParams)
    if (!isRecord(parsed)) return undefined
    const code = parsed.currencyCode
    return typeof code === 'string' && code.length > 0 ? code : undefined
  } catch {
    return undefined
  }
}

interface PriceAndCurrency {
  displayPrice?: number
  regularPrice?: number
  isLuxPlusFare?: boolean
  priceCurrency?: string
}

// Pairs price+currency from one source (refresh or saved), never mixed.
function resolveShortlistedFlightPriceAndCurrency(args: {
  isReturn: boolean
  savedItemData: LeFlightSavedItemData | undefined
  refreshedData: RefreshedFlight | undefined
}): PriceAndCurrency {
  const { isReturn, savedItemData, refreshedData } = args
  if (!savedItemData) return {}

  const savedPrice = isReturn ? savedItemData.farePrice : savedItemData.price
  const savedRegularPrice = savedItemData.regularPrice
  const savedCurrency =
    savedItemData.currencyCode
    ?? savedItemData.fareCurrencyCode
    ?? parseSearchParamsCurrency(savedItemData.searchParams)

  const refreshHasPrice = refreshedData?.newPrice !== undefined
  const displayPrice = refreshHasPrice ? refreshedData?.newPrice : savedPrice
  const regularPrice = refreshHasPrice ? refreshedData?.newRegularPrice : savedRegularPrice
  const isLuxPlusFare = refreshHasPrice ? refreshedData?.isLuxPlusFare : savedItemData.isLuxPlusFare
  const priceCurrency = refreshHasPrice
    ? refreshedData?.newCurrencyCode
    : savedCurrency

  return { displayPrice, regularPrice, isLuxPlusFare, priceCurrency }
}

interface ResolveDrawerPriceArgs {
  isReturn: boolean
  departingJourney: App.JourneyV2
  returningJourney?: App.JourneyV2
  refreshedData?: RefreshedFlight
}

interface DrawerPriceAndCurrency {
  displayPrice?: number
  currencyCode?: string
  regularPrice?: number
  isLuxPlusFare?: boolean
}

// Pairs price+currency for the flight details drawer.
function resolveDrawerFlightPriceAndCurrency(args: ResolveDrawerPriceArgs): DrawerPriceAndCurrency {
  const { isReturn, departingJourney, returningJourney, refreshedData } = args

  const isLuxPlusFare = departingJourney.bookingInfo?.isLuxPlusFare
  const departingRegularPrice = departingJourney.regularPrice?.adult?.totalFare
  const returningRegularPrice = returningJourney?.regularPrice?.adult?.totalFare
  // Only surface a struck-through public total when EVERY leg has a finite
  // regular fare. Coercing a missing leg to 0 would render a public "was" lower
  // than the real total (the member total still covers both legs) — a
  // misleading discount. On partial data, fall back to the plain price.
  const regularPrice = isReturn
    ? departingRegularPrice !== undefined && returningRegularPrice !== undefined
      ? departingRegularPrice + returningRegularPrice
      : undefined
    : departingRegularPrice

  if (refreshedData?.newPrice !== undefined) {
    return {
      displayPrice: refreshedData.newPrice,
      currencyCode: refreshedData.newCurrencyCode,
      regularPrice,
      isLuxPlusFare,
    }
  }

  const departingFare = departingJourney.price.adult.totalFare
  const returningFare = returningJourney?.price.adult.totalFare

  if (typeof departingFare !== 'number' || (isReturn && typeof returningFare !== 'number')) {
    return { displayPrice: undefined, currencyCode: departingJourney.currencyCode, regularPrice, isLuxPlusFare }
  }

  return {
    displayPrice: isReturn ? departingFare + (returningFare ?? 0) : departingFare,
    currencyCode: departingJourney.currencyCode,
    regularPrice,
    isLuxPlusFare,
  }
}

export {
  parseSearchParams,
  isBookedFlightOption,
  parseSearchParamsCurrency,
  resolveShortlistedFlightPriceAndCurrency,
  resolveDrawerFlightPriceAndCurrency,
}
